import type { StatusChipVariant } from '@/components/ui/status-chip';
import type { FeeSettlementStatus } from '@/services/api/broker-fee-settlements';
import type { BrokerType, FeeBandState, FeeSplit, WireFeeBand } from '@/services/api/brokers';
import { formatDateOnly, formatTieredNumber } from '@/utils/format';

// Detail-page formatters. The broker-wide
// canonical helpers already live in the batches feature and are imported at use
// sites: batch-formatters.ts (formatBrokerDateTime/formatAmount) and batch-status.ts
// (transactionStatusVariant, batchStatusVariant).

/** x-small flat chip sizing shared across the detail tables. */
export const CHIP_XS_CLASS = 'px-2 py-0.5 text-xs';

/**
 * Tiered precision, en-GB grouping (≥100 → 2 dp, ≥1 → 4 dp, else 8 dp).
 * Delegates to the shared formatTieredNumber — same thresholds, same
 * '0' short-circuit; identical output byte-for-byte.
 */
export const formatNumber = formatTieredNumber;

/** "02 Aug 2026" (no time) — stats card subtitles; unparseable input falls back to the raw string. */
export const formatDateShort = (value: string): string => formatDateOnly(value, value);

/**
 * Fee Split stats-card subtitle: same parts-joining as the
 * list chip label but WITHOUT the " fee split" suffix, and 'No rates configured'
 * when the parts are empty — which is also how an empty percentage split renders
 * everywhere (chosen fix).
 */
export const feeSplitSummary = (feeSplit: FeeSplit): string => {
  const parts: string[] = [];
  if (feeSplit.type === 'percentage') {
    if (feeSplit.trades !== undefined) parts.push(`T: ${feeSplit.trades}%`);
    if (feeSplit.liquidations !== undefined) parts.push(`L: ${feeSplit.liquidations}%`);
  } else {
    parts.push(`M: ${feeSplit.maker_fee_bps} bps`);
    parts.push(`T: ${feeSplit.taker_fee_bps} bps`);
    if (feeSplit.liquidations !== undefined) parts.push(`L: ${feeSplit.liquidations}%`);
  }
  return parts.length === 0 ? 'No rates configured' : parts.join(' / ');
};

const FULL_SHARE_PERCENT = 100;

/** "Broker 20% / OT 80%", or "OT 100%" when the rate is unset. */
export const brokerOtSplitLabel = (value: number | undefined): string => (value === undefined ? 'OT 100%' : `Broker ${value}% / OT ${FULL_SHARE_PERCENT - value}%`);

/**
 * `formatFeeBandFees`: FIXED with a non-array
 * fee_structure → the bps number as string; VOLUME → the literal 'Volume-tiered';
 * anything else → '-'.
 */
export const formatFeeBandFee = (band: WireFeeBand, key: 'maker_fee_bps' | 'taker_fee_bps'): string => {
  if (band.type === 'FIXED' && !Array.isArray(band.fee_structure)) return String(band.fee_structure[key]);
  if (band.type === 'VOLUME') return 'Volume-tiered';
  return '-';
};

const FEE_BAND_STATE_VARIANTS: Record<FeeBandState, StatusChipVariant> = {
  PENDING: 'warning',
  ACTIVE: 'success',
  REJECTED: 'destructive',
};

/** Fallback grey for unknown states. */
export const feeBandStateVariant = (state: string): StatusChipVariant => FEE_BAND_STATE_VARIANTS[state as FeeBandState] ?? 'muted';

/** FIXED gets the stock info chip; VOLUME is purple, which StatusChip lacks — class override. */
export const FEE_BAND_VOLUME_CHIP_CLASS = 'border-purple-500/40 bg-purple-500/10 text-purple-400';

export const BROKER_TYPE_CHIP_VARIANTS: Record<BrokerType, StatusChipVariant> = {
  TIED_AGENT: 'info',
  BROKER: 'warning',
};

const FEE_SETTLEMENT_STATUS_VARIANTS: Record<FeeSettlementStatus, StatusChipVariant> = {
  PENDING_REVIEW: 'warning',
  PAYMENT_INITIATED: 'info',
  PAYMENT_PENDING: 'info',
  PAID: 'success',
  REJECTED: 'destructive',
  FAILED: 'destructive',
};

/** Fallback grey for unknown statuses. */
export const feeSettlementStatusVariant = (status: string): StatusChipVariant => FEE_SETTLEMENT_STATUS_VARIANTS[status as FeeSettlementStatus] ?? 'muted';
